import React, { useState, useEffect } from "react";
import {
  Table,
  Form,
  Select,
  Button,
  message,
  Tag,
  Modal,
  Input,
  Space,
  Avatar,
  Row,
  Col,
} from "antd";
import { userService } from "../../services/user.service";
import FilterForm from "../../components/FilterForm";

function Users() {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchParams] = Form.useForm();
  const [form] = Form.useForm();
  const [createForm] = Form.useForm();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [userNames, setUserNames] = useState([]);
  const [passwordValid, setPasswordValid] = useState(false);
  const pageSize = 10;

  const roles = [
    { value: 0, label: "管理员" },
    { value: 1, label: "内业" },
    { value: 2, label: "外业" },
  ];

  const createModalRoles = [
    { value: 1, label: "内业" },
    { value: 2, label: "外业" },
  ];

  const fetchNicknameOptions = async () => {
    try {
      const response = await userService.fetchNicknameOptions();
      if (response.code === 0) {
        setUserNames(response.data);
      } else {
        message.error(response.message || "获取用户姓名列表失败");
      }
    } catch (error) {
      message.error("获取用户姓名列表失败：" + error.response?.data?.message ||  error.message);
    }
  };

  const fetchUsers = async (params = {}) => {
    setLoading(true);
    try {
      const response = await userService.fetchUsers({
        page: params.page || currentPage,
        limit: pageSize,
        type: params.role,
        user_id: params.name,
      });

      if (response.code === 0) {
        setData(response.data.list.map((item) => ({ ...item, key: item.id })));
        setTotal(response.data.total);
      } else {
        message.error(response.message || "获取用户列表失败");
      }
    } catch (error) {
      message.error("获取用户列表失败：" + error.response?.data?.message ||  error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchNicknameOptions();
  }, []);

  const handleCreateUser = async (values) => {
    try {
      const response = await userService.createUser(values);

      if (response.code === 0) {
        message.success("创建用户成功");
        setIsModalVisible(false);
        createForm.resetFields();
        fetchUsers();
      } else {
        message.error("创建用户失败：" + response.message);
      }
    } catch (error) {
      message.error("调用接口失败" + error.response?.data?.message ||  error.message);
    }
  };

  const handleResetPassword = async (userId) => {
    setSelectedUserId(userId);
    setIsPasswordModalVisible(true);
  }

  const handleUpdateUserStatus = async (userId, status) => {
    try {
      const response = await userService.updateUserStatus(userId, status);

      if (response.code === 0) {
        message.success(status === 1 ? "恢复用户成功" : "失效用户成功");
        fetchUsers();
      } else {
        message.error(
          response.message || (status === 1 ? "恢复用户失败" : "失效用户失败")
        );
      }
    } catch (error) {
      message.error(
        (status === 1 ? "恢复用户失败" : "失效用户失败") + "：" + error.response?.data?.message ||  error.message
      );
    }
  };

  const columns = [
    {
      title: "用户ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "姓名",
      dataIndex: "nickname",
      key: "nickname",
    },
    {
      title: "用户名",
      dataIndex: "username",
      key: "username",
    },
    {
      title: "角色",
      dataIndex: "type",
      key: "type",
      render: (role) => {
        let color = "";
        let roleText = "";

        switch (Number(role)) {
          case 0:
            color = "#f56a00";
            roleText = "管理员";
            break;
          case 1:
            color = "#87d068";
            roleText = "内业";
            break;
          case 2:
            color = "#1677ff";
            roleText = "外业";
            break;
          default:
            color = "#d9d9d9";
            roleText = "未知";
        }

        return (
          <Avatar style={{ backgroundColor: color }}>
            {roleText.slice(0, 1)}
          </Avatar>
        );
      },
    },
    {
      title: "状态",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const statusText = status === 1 ? "有效" : "已失效";
        const color = status === 1 ? "#52c41a" : "#d9d9d9";
        return (
          <Tag
            color={color}
            style={{ minWidth: "50px", textAlign: "center", padding: "0 15px" }}
          >
            {statusText}
          </Tag>
        );
      },
    },
    {
      title: "操作",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          {record.type !== 0 && (
            <Button
            size="small"
              type="link"
              onClick={() => {
                setSelectedUserId(record.id);
                setIsPasswordModalVisible(true);
              }}
            >
              修改密码
            </Button>
          )}
          {record.type !== 0 && record.status === 1 && (
            <Button
            size="small"
              type="link"
              danger
              onClick={() => handleUpdateUserStatus(record.id, 0)}
            >
              失效
            </Button>
          )}
          {record.type !== 0 && record.status === 0 && (
            <Button
            size="small"
              type="link"
              onClick={() => handleUpdateUserStatus(record.id, 1)}
            >
              恢复
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const handleSearch = (values) => {
    const sp = {
      page: 1,
      role: values.role,
      name: values.name,
    };
    setCurrentPage(1);
    fetchUsers(sp);
  };

  const handleReset = () => {
    searchParams.resetFields();
    setCurrentPage(1);
    fetchUsers();
  };

  const handleUpdatePassword = async (password) => {
    try {
      const response = await userService.updatePassword(
        selectedUserId,
        password
      );

      if (response.code === 0) {
        message.success("密码修改成功");
        setIsPasswordModalVisible(false);
        form.resetFields(["password"]);
      } else {
        message.error(response.message || "密码修改失败");
      }
    } catch (error) {
      message.error("密码修改失败：" + error.response?.data?.message ||  error.message);
    }
  };

  const validatePassword = (_, value) => {
    if (!value) {
      return Promise.reject();
    }

    let types = 0;
    if (/[A-Z]/.test(value)) types++;
    if (/[a-z]/.test(value)) types++;
    if (/[0-9]/.test(value)) types++;
    if (/[^A-Za-z0-9]/.test(value)) types++;

    if (types < 2) {
      return Promise.reject(
        "密码必须包含至少两种字符类型（大写/小写/数字/符号）"
      );
    }

    return Promise.resolve();
  };

  return (
    <div>
      <Row
        gutter={16}
        style={{ marginBottom: 12, padding: "0 12px" }}
        align={"middle"}
        justify={"space-between"}
      >
        <h2 style={{ margin: 0 }}>用户管理</h2>
        <Button type="primary" onClick={() => setIsModalVisible(true)}>
          创建用户
        </Button>
      </Row>
      <FilterForm onFinish={handleSearch} onReset={handleReset} form={searchParams}>
        <Form.Item
          name="role"
          label="角色"
          style={{ flex: "1 1 200px", marginBottom: 0, maxWidth: "200px" }}
        >
          <Select placeholder="请选择角色" allowClear style={{ width: "100%" }}>
            {roles.map((role) => (
              <Select.Option key={role.value} value={role.value}>
                {role.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="name"
          label="姓名"
          style={{ flex: "1 1 200px", marginBottom: 0, maxWidth: "200px" }}
        >
          <Select placeholder="请选择姓名" allowClear style={{ width: "100%" }}>
            {userNames.map((user) => (
              <Select.Option key={user.id} value={user.id}>
                {user.nickname}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </FilterForm>
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        size="small"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          onChange: (page) => {
            setCurrentPage(page);
            const currentFormValues = form.getFieldsValue();
            fetchUsers({ ...currentFormValues, page });
          },
          showTotal: (total) => `共 ${total} 条数据`,
          position: ["bottomRight"],
        }}
      />

      <Modal
        title="创建用户"
        open={isModalVisible}
        onOk={() => createForm.submit()}
        onCancel={() => {
          setIsModalVisible(false);
          createForm.resetFields();
        }}
        okText="确认创建"
        cancelText="取消"
      >
        <Form
          form={createForm}
          onFinish={handleCreateUser}
          layout="vertical"
          autoComplete="off"
        >
          <Form.Item
            name="type"
            label="角色"
            initialValue={2}
            rules={[{ required: true, message: "请选择角色" }]}
          >
            <Select placeholder="请选择角色">
              {createModalRoles.map((role) => (
                <Select.Option key={role.value} value={role.value}>
                  {role.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="nickname"
            label="姓名"
            rules={[
              { required: true, message: "请输入姓名" },
              { max: 20, message: "姓名不能超过20个字" },
            ]}
          >
            <Input placeholder="请输入姓名" autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="mobile"
            label="手机号"
            rules={[
              { required: true, message: "请输入手机号" },
              { pattern: /^1[3-9]\d{9}$/, message: "请输入正确的手机号格式" },
            ]}
            onChange={(e) => {
              const filteredValue = e.target.value.replace(/\D/g, '');
              if (e.target.value !== filteredValue) {
                createForm.setFieldValue("mobile", filteredValue);
              }
            }}
          >
            <Input showCount maxLength={11} placeholder="请输入手机号" autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="username"
            label={
              <>
                用户名
                <div
                  style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}
                ></div>
              </>
            }
            rules={[
              { required: true, message: "请输入用户名" },
              { max: 20, message: "用户名不能超过20个字" },
              {
                pattern: /^[a-z0-9]+$/,
                message: "用户名只能包含小写字母和数字",
              },
            ]}
            onChange={(e) => {
              const filteredValue = e.target.value.replace(/[^a-z0-9]/g, "");
              if (e.target.value !== filteredValue) {
                createForm.setFieldValue("username", filteredValue);
              }
            }}
          >
            <Input placeholder="请输入用户名" autoComplete="new-password" />
          </Form.Item>

          <Form.Item
            name="password"
            label={
              <>
                初始密码
                <div
                  style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}
                ></div>
              </>
            }
            rules={[
              { required: true, message: "请输入初始密码" },
              { min: 8, message: "密码长度至少为8位" },
              { validator: validatePassword },
            ]}
          >
            <Input.Password
              placeholder="请输入初始密码"
              autoComplete="new-password"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="修改密码"
        open={isPasswordModalVisible}
        onOk={() =>
          form.validateFields().then((values) => {
            Modal.confirm({
              title: '确认修改密码',
              content: '确定要修改该用户的密码吗？',
              okText: '确认',
              cancelText: '取消',
              onOk: () => {
                handleUpdatePassword(values.password);
                form.resetFields(['password']);
              }
            });
          })
        }
        onCancel={() => {
          setIsPasswordModalVisible(false);
          form.resetFields(["password"]);
          setPasswordValid(false);
        }}
        okText="确认修改"
        cancelText="取消"
        okButtonProps={{
          disabled: !passwordValid,
        }}
      >
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          onValuesChange={(_, values) => {
            const password = values.password;
            if (password && password.length >= 8) {
              let types = 0;
              if (/[A-Z]/.test(password)) types++;
              if (/[a-z]/.test(password)) types++;
              if (/[0-9]/.test(password)) types++;
              if (/[^A-Za-z0-9]/.test(password)) types++;
              setPasswordValid(types >= 2);
            } else {
              setPasswordValid(false);
            }
          }}
        >
          <Form.Item
            name="password"
            label="新密码"
            rules={[
              { required: true, message: "请输入新密码" },
              { min: 8, message: "密码长度至少为8位" },
              { validator: validatePassword },
            ]}
          >
            <Input.Password
              placeholder="请输入新密码"
              autoComplete="new-password"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default Users;
